import React, { useEffect, useState } from 'react';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { RefreshCw, Clock, StopCircle, X } from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranscription } from '../../hooks/useTranscription';
interface ProcessingCardProps {
  startTime: number;
  videoId?: string;
  url?: string;
  taskId: string;
}
export function ProcessingCard({
  startTime,
  videoId,
  url,
  taskId
}: ProcessingCardProps) {
  const navigate = useNavigate();
  const {
    stopTranscription
  } = useTranscription();
  const [elapsedTime, setElapsedTime] = useState<string>('00:00');
  const [isHovering, setIsHovering] = useState(false);
  const [isStopping, setIsStopping] = useState(false);
  useEffect(() => {
    const intervalId = setInterval(() => {
      const now = Date.now();
      const elapsed = Math.floor((now - startTime) / 1000); // elapsed time in seconds
      const minutes = Math.floor(elapsed / 60).toString().padStart(2, '0');
      const seconds = (elapsed % 60).toString().padStart(2, '0');
      setElapsedTime(`${minutes}:${seconds}`);
    }, 1000);
    return () => clearInterval(intervalId);
  }, [startTime]);
  const handleStopTranscription = async (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (isStopping) return;
    setIsStopping(true);
    try {
      await stopTranscription(taskId);
    } finally {
      setIsStopping(false);
    }
  };
  const handleCardClick = () => {
    navigate(`/app/transcriptions/${taskId}`);
  };
  return <Card className="h-full transition-all duration-200 cursor-pointer hover:border-primary/40 hover:shadow-md hover:shadow-primary/5 relative overflow-hidden" onClick={handleCardClick} onMouseEnter={() => setIsHovering(true)} onMouseLeave={() => setIsHovering(false)} data-id="element-1354">
      <div className="w-full h-32 sm:h-40 bg-gray-800 rounded-t-lg flex items-center justify-center" data-id="element-1355">
        <div className="flex flex-col items-center justify-center" data-id="element-1356">
          <RefreshCw size={32} className="text-primary animate-spin mb-2" data-id="element-1357" />
          <p className="text-sm text-gray-300" data-id="element-1358">Processing Video</p>
          <p className="text-xs text-gray-400 mt-1" data-id="element-1359">
            Extracting audio and metadata...
          </p>
        </div>
      </div>
      {/* Stop button overlay that appears on hover */}
      {isHovering && <div className="absolute inset-0 bg-black/70 flex items-center justify-center transition-opacity duration-200" data-id="element-1360">
          <Button variant="destructive" size="sm" className="flex items-center gap-1.5" onClick={handleStopTranscription} disabled={isStopping} data-id="element-1361">
            {isStopping ? <>
                <RefreshCw size={16} className="animate-spin" data-id="element-1362" />
                Stopping...
              </> : <>
                <StopCircle size={16} data-id="element-1363" />
                Stop Transcription
              </>}
          </Button>
        </div>}
      <CardContent className="p-3 md:p-4" data-id="element-1364">
        <div className="flex justify-between items-center mb-2" data-id="element-1365">
          <div className="flex items-center text-xs text-muted-foreground" data-id="element-1366">
            <Clock size={12} className="mr-1" data-id="element-1367" />
            <span data-id="element-1368">{elapsedTime}</span>
          </div>
          <div data-id="element-1369">
            <span className="flex items-center gap-1 text-xs font-medium text-primary bg-primary/10 px-2 py-0.5 rounded-full" data-id="element-1370">
              <RefreshCw size={12} className="animate-spin" data-id="element-1371" />
              <span data-id="element-1372">Processing</span>
            </span>
          </div>
        </div>
        <h3 className="font-medium text-sm md:text-base line-clamp-2 mb-1" data-id="element-1373">
          {url ? `Processing: ${url}` : `Processing video...`}
        </h3>
        <p className="text-xs text-muted-foreground truncate" data-id="element-1374">
          ID: {videoId || taskId}
        </p>
        <div className="mt-2" data-id="element-1375">
          <div className="w-full h-1.5 bg-secondary rounded-full overflow-hidden" data-id="element-1376">
            <div className="h-full bg-primary rounded-full animate-pulse" data-id="element-1377"></div>
          </div>
        </div>
      </CardContent>
    </Card>;
}